import { UPLOADS_BASE } from './constants';

const IS_DEV = process.env.NODE_ENV === 'development';
let _failedCount = 0;

/**
 * Call from img onError handlers to log broken image URLs in development.
 * Tracks a running count of fallback activations across the session.
 */
export const logImageError = (failedSrc) => {
  if (!IS_DEV) return;
  _failedCount += 1;
  console.warn(`[Image] Load failed (fallback #${_failedCount}): ${failedSrc || '(empty src)'}`);
};

/**
 * Resolve an article image URL to a fully-qualified, browser-loadable URL.
 *
 * Rules:
 *  - Trims leading/trailing whitespace before processing.
 *  - data:/blob: URIs pass through unchanged (inline/uploaded binary content).
 *  - Protocol-relative URLs (//cdn.example.com/img) → https:// so they always
 *    load correctly regardless of whether the page is served over HTTP or HTTPS.
 *  - localhost /uploads/ URLs in production → rewritten to UPLOADS_BASE so
 *    articles created in local dev still render on the live server.
 *  - http:// → https:// upgrade to prevent mixed-content blocking on HTTPS hosts.
 *  - Relative /uploads/ paths are prefixed with UPLOADS_BASE so they resolve
 *    to the backend server instead of the frontend domain.
 *  - Any other relative path is returned as-is.
 */
export const resolveImageUrl = (url) => {
  if (!url) return '';
  const src = typeof url === 'string' ? url.trim() : '';
  if (!src) return '';

  // Inline / uploaded binary content — never transform
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;

  // Protocol-relative URL (e.g. //cdn.example.com/img.jpg) → force https
  if (src.startsWith('//')) {
    const resolved = `https:${src}`;
    if (IS_DEV) console.debug('[Image] Resolved protocol-relative URL:', resolved);
    return resolved;
  }

  // In production: rewrite localhost /uploads/ URLs to the real uploads origin.
  // Articles created during local development store URLs like http://localhost:5000/uploads/...
  // — these are dead on the live server and must be rewritten using UPLOADS_BASE.
  if (!IS_DEV && UPLOADS_BASE) {
    const localMatch = src.match(/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/uploads\/.+)$/i);
    if (localMatch) return `${UPLOADS_BASE}${localMatch[3]}`;
  }

  // Upgrade http → https to prevent mixed-content blocking on https deployments.
  // Exclude localhost / 127.x — local dev servers don't have SSL and must stay http.
  if (src.startsWith('http://') && !/^http:\/\/(localhost|127\.0\.0\.1)([:/]|$)/i.test(src)) {
    const resolved = `https://${src.slice(7)}`;
    if (IS_DEV) console.debug('[Image] Upgraded http→https:', resolved);
    return resolved;
  }

  // Already a valid absolute https URL — pass through
  if (src.startsWith('https://')) return src;

  // Admin-uploaded images: /uploads/filename → absolute backend URL
  if (src.startsWith('/uploads/') && UPLOADS_BASE) return `${UPLOADS_BASE}${src}`;

  // Any other relative or unknown path — return as-is
  return src;
};
